import React, { useState, useEffect } from "react";
import {
  Calendar,
  Filter,
  Download,
  Search,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import {
  reportsService,
  BookingReportFilters,
  BookingReportItem,
} from "../../services/reportsService";
import hotelService from "../../services/hotelService";
import Autocomplete from "../../components/ui/autocomplete";
import Pagination from "../../components/ui/pagination";

const BookingReports: React.FC = () => {
  const [filters, setFilters] = useState<BookingReportFilters>({
    page: 1,
    limit: 50,
  });
  const [bookings, setBookings] = useState<BookingReportItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [hotels, setHotels] = useState<any[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [states, setStates] = useState<string[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    total_pages: 0,
  });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const bookingStatuses = [
    { value: "confirmed", label: "Confirmed" },
    { value: "booked (HCN Pending)", label: "Booked (HCN Pending)" },
    { value: "pending (HBR Pending)", label: "Pending (HBR Pending)" },
    { value: "cancelled", label: "Cancelled" },
    { value: "no_show", label: "No Show" },
    { value: "checked_in", label: "Checked In" },
    { value: "checked_out", label: "Checked Out" },
  ];

  const paymentStatuses = [
    { value: "pending", label: "Pending" },
    { value: "paid", label: "Paid" },
    { value: "failed", label: "Failed" },
    { value: "refunded", label: "Refunded" },
  ];

  useEffect(() => {
    fetchHotels();
    fetchBookings();
  }, []);

  const fetchHotels = async () => {
    try {
      const response = await hotelService.getHotels(1, 1000);
      setHotels(response.data);

      // Extract unique cities, states, and countries
      const uniqueCities = Array.from(
        new Set(response.data.map((hotel: any) => hotel.city).filter(Boolean))
      ).sort();
      const uniqueStates = Array.from(
        new Set(response.data.map((hotel: any) => hotel.state).filter(Boolean))
      ).sort();
      const uniqueCountries = Array.from(
        new Set(
          response.data.map((hotel: any) => hotel.country).filter(Boolean)
        )
      ).sort();

      setCities(uniqueCities);
      setStates(uniqueStates);
      setCountries(uniqueCountries);
    } catch (error) {
      console.error("Error fetching hotels:", error);
    }
  };

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const response = await reportsService.getBookingReport(filters);
      setBookings(response.data.bookings);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error("Error fetching booking report:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: 1, // Reset to first page when filters change
    }));
  };

  const handleSearch = () => {
    fetchBookings();
  };

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
    fetchBookings();
  };

  const handleItemsPerPageChange = (newLimit: number) => {
    setFilters((prev) => ({ ...prev, limit: newLimit, page: 1 }));
    fetchBookings();
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency || "INR",
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-IN");
  };

  const getStatusBadge = (status: string) => {
    const statusColors: { [key: string]: string } = {
      confirmed: "bg-green-100 text-green-800",
      pending: "bg-yellow-100 text-yellow-800",
      cancelled: "bg-red-100 text-red-800",
      no_show: "bg-gray-100 text-gray-800",
      checked_in: "bg-blue-100 text-blue-800",
      checked_out: "bg-purple-100 text-purple-800",
    };

    return (
      <span
        className={`px-2 py-1 text-xs font-medium rounded-full ${
          statusColors[status] || "bg-gray-100 text-gray-800"
        }`}
      >
        {status.replace("_", " ").toUpperCase()}
      </span>
    );
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center space-x-3 mb-2">
          <Calendar className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900">Booking Reports</h1>
        </div>
        <p className="text-gray-600">
          Comprehensive booking reports with advanced filtering options
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
          </div>
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-700"
          >
            <span>
              {showAdvancedFilters ? "Hide" : "Show"} Advanced Filters
            </span>
            {showAdvancedFilters ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>
        </div>

        {/* Basic Filters */}
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            Basic Filters
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hotel
              </label>
              <Autocomplete
                options={hotels.map((hotel) => ({
                  value: hotel.id,
                  label: `${hotel.hotel_name} - ${hotel.city}, ${hotel.country}`,
                }))}
                value={filters.hotel_id}
                onChange={(value) => handleFilterChange("hotel_id", value)}
                placeholder="Search hotels..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Checkin Date From
              </label>
              <input
                type="date"
                value={filters.checkin_date_from || ""}
                onChange={(e) =>
                  handleFilterChange("checkin_date_from", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Checkin Date To
              </label>
              <input
                type="date"
                value={filters.checkin_date_to || ""}
                onChange={(e) =>
                  handleFilterChange("checkin_date_to", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Advanced Filters */}
        {showAdvancedFilters && (
          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">
              Advanced Filters
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <Autocomplete
                  options={cities.map((city) => ({
                    value: city,
                    label: city,
                  }))}
                  value={filters.city}
                  onChange={(value) => handleFilterChange("city", value)}
                  placeholder="Search cities..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State
                </label>
                <Autocomplete
                  options={states.map((state) => ({
                    value: state,
                    label: state,
                  }))}
                  value={filters.state}
                  onChange={(value) => handleFilterChange("state", value)}
                  placeholder="Search states..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country
                </label>
                <Autocomplete
                  options={countries.map((country) => ({
                    value: country,
                    label: country,
                  }))}
                  value={filters.country}
                  onChange={(value) => handleFilterChange("country", value)}
                  placeholder="Search countries..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Booking Date From
                </label>
                <input
                  type="date"
                  value={filters.booking_date_from || ""}
                  onChange={(e) =>
                    handleFilterChange("booking_date_from", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Booking Date To
                </label>
                <input
                  type="date"
                  value={filters.booking_date_to || ""}
                  onChange={(e) =>
                    handleFilterChange("booking_date_to", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Booking Status
                </label>
                <select
                  value={filters.booking_status || ""}
                  onChange={(e) =>
                    handleFilterChange(
                      "booking_status",
                      e.target.value || undefined
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Statuses</option>
                  {bookingStatuses.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Status
                </label>
                <select
                  value={filters.payment_status || ""}
                  onChange={(e) =>
                    handleFilterChange(
                      "payment_status",
                      e.target.value || undefined
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Payment Statuses</option>
                  {paymentStatuses.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200">
          <button
            onClick={handleSearch}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Search className="w-4 h-4" />
            <span>{loading ? "Searching..." : "Search"}</span>
          </button>

          <button
            onClick={() => {
              setFilters({ page: 1, limit: 50 });
              fetchBookings();
            }}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Results */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              Booking Report Results
            </h3>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">
                Total: {pagination.total} bookings
              </span>
              <button className="flex items-center space-x-2 px-3 py-1 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200">
                <Download className="w-4 h-4" />
                <span>Export</span>
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading bookings...</p>
          </div>
        ) : bookings.length === 0 ? (
          <div className="p-8 text-center">
            <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">
              No bookings found matching your criteria
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Booking Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Hotel & Room
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Guest Info
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Dates
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {bookings.map((booking) => (
                    <tr key={booking.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {booking.booking_reference}
                          </div>
                          <div className="text-sm text-gray-500">
                            Booked on {formatDate(booking.booking_datetime)}
                          </div>
                          {booking.client_reference && (
                            <div className="text-xs text-gray-400">
                              Client: {booking.client_reference}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {booking.hotel_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {booking.room_type_name} - {booking.plan_name}
                          </div>
                          <div className="text-xs text-gray-400">
                            {booking.city}, {booking.country}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {booking.guest_first_name} {booking.guest_last_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {booking.guest_email}
                          </div>
                          <div className="text-xs text-gray-400">
                            {booking.adults_count} Adults,{" "}
                            {booking.children_count} Children
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm text-gray-900">
                            Check-in: {formatDate(booking.checkin_date)}
                          </div>
                          <div className="text-sm text-gray-900">
                            Check-out: {formatDate(booking.checkout_date)}
                          </div>
                          <div className="text-xs text-gray-400">
                            {booking.nights_count} nights
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(
                            booking.total_amount,
                            booking.currency
                          )}
                        </div>
                        <div className="text-xs text-gray-500">
                          {booking.rooms_count} room(s)
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          {getStatusBadge(booking.booking_status)}
                          <div className="text-xs text-gray-500">
                            Payment: {booking.payment_status}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Enhanced Pagination */}
            {pagination.total > 0 && (
              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.total_pages}
                totalItems={pagination.total}
                itemsPerPage={pagination.limit}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                showItemsPerPage={true}
                itemsPerPageOptions={[10, 25, 50, 100]}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BookingReports;
